/**
 * MovieExploreEmptyState Component
 * Displays example movie constellations to help users get started
 */

import { useMovieExploreStore } from '../../stores/movie-explore-store';
import { EXAMPLE_MOVIE_CONSTELLATIONS } from '../../constants/example-movie-constellations';
import EmptyState from '../EmptyState';

const NODE_ICONS: Record<string, string> = {
  director: '🎬',
  genre: '🎭',
  mood: '💭',
  cinematographer: '📷',
  country: '🌍',
  era: '📅',
  movement: '🌊',
  theme: '✨',
  actor: '👤',
  studio: '🏛️',
};

export default function MovieExploreEmptyState() {
  const { loadConstellation } = useMovieExploreStore();

  const handleLoadExample = (id: string) => {
    const constellation = EXAMPLE_MOVIE_CONSTELLATIONS.find((c) => c.id === id);
    if (constellation) {
      loadConstellation(constellation);
    }
  };

  return (
    <div className="space-y-6">
      <EmptyState
        icon="🎞️"
        title="Build your programme."
        description="Map the influences. See where the signal leads. Or start with one of these."
      />

      {/* Example Constellations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {EXAMPLE_MOVIE_CONSTELLATIONS.map((constellation) => (
          <button
            key={constellation.id}
            onClick={() => handleLoadExample(constellation.id)}
            className="bg-white rounded-lg shadow-md p-4 text-left hover:shadow-lg hover:scale-[1.02] transition-all border-2 border-transparent hover:border-blue-500"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">{constellation.name}</h3>
              <span className="text-2xl">
                {NODE_ICONS[constellation.nodes[0].type] || '🎞️'}
              </span>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600 mb-3">{constellation.description}</p>

            {/* Node Preview */}
            <div className="flex flex-wrap gap-1 mb-3">
              {constellation.nodes.slice(0, 4).map((node, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                >
                  {NODE_ICONS[node.type] || '✨'} {node.value}
                </span>
              ))}
              {constellation.nodes.length > 4 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                  +{constellation.nodes.length - 4} more
                </span>
              )}
            </div>

            {/* Vibe Preview */}
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <span>⏱️</span>
                <span>Pacing {(constellation.vibe.pacing * 10).toFixed(0)}/10</span>
              </div>
              <div className="flex items-center gap-1">
                <span>🌡️</span>
                <span>Tone {(constellation.vibe.tone * 10).toFixed(0)}/10</span>
              </div>
              <div className="flex items-center gap-1">
                <span>🔍</span>
                <span>Obscurity {(constellation.vibe.obscurity * 10).toFixed(0)}/10</span>
              </div>
            </div>

            {/* Load CTA */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <span className="text-sm font-medium text-blue-600">
                Click to load →
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
